import { useQueryClient } from '@tanstack/react-query';
import { isBefore, isSameDay, parseISO, startOfDay } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { useCartNextAvailableDate } from '~shared/features/cart/hooks/useCartNextAvailableDate';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { fetchCheckoutNextAvailableDates } from '~shared/features/next-available-date/queries/fetchCheckoutNextAvailableDates';
import { useNextAvailableDatesByPrescriptionIdFromQueryCache } from '~shared/features/next-available-date/queries/useNextAvailableDatesByPrescriptionIdFromQueryCache';
import { getDispensingFeeAnalyticsParams } from '~shared/features/pricing/programDispensingFee';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getOrigin from '~shared/lib/analytics/src/getOrigin';
import { useOrderPricing } from '~shared/queries';
import { Sentry } from '~shared/sentry';
import { useDispatchShared, useStore } from '~shared/store';
import { type ReduxStateShared } from '~shared/types';

export const useCartViewed = () => {
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const store = useStore();
  const { cartItems } = useCartData();
  const {
    earliestAvailableDate: cartEarliestAvailableDate,
    earliestAvailableDateReason: cartEarliestAvailableDateReason,
    earliestAvailableDateInternalReason: cartEarliestAvailableDateInternalReason,
    lastRequestNextAvailableDateAttemptedAt,
  } = useCartNextAvailableDate();
  const { orderPricing } = useOrderPricing();
  const origin = getOrigin();
  const cartItemResourceIds = useMemo(() => cartItems.map((item) => item.resource_id), [cartItems]);
  const nextAvailableDateAddressID = useNextAvailableDateAddressId();
  const nextAvailableDatesByPrescriptionID = useNextAvailableDatesByPrescriptionIdFromQueryCache();
  const isNextAvailableDateValid =
    !!cartEarliestAvailableDate && !isBefore(parseISO(cartEarliestAvailableDate), startOfDay(new Date()));
  const isPricePending = orderPricing?.total?.info_key === 'price_unavailable';
  const dispensingFeeParams = useMemo(
    () => getDispensingFeeAnalyticsParams(orderPricing?.line_items),
    [orderPricing?.line_items],
  );

  // Find the bottleneck prescriptions that are causing the the delay of the earliest available date
  const bottleneckPrescriptionsIds = useMemo(
    () =>
      cartItemResourceIds.reduce<number[]>((bottleneckPrescriptionsIds, prescriptionId) => {
        const ead = nextAvailableDatesByPrescriptionID[prescriptionId]?.earliest?.date;
        if (ead && isSameDay(parseISO(ead), parseISO(cartEarliestAvailableDate))) {
          bottleneckPrescriptionsIds.push(prescriptionId);
        }
        return bottleneckPrescriptionsIds;
      }, []),
    [cartItemResourceIds, cartEarliestAvailableDate, nextAvailableDatesByPrescriptionID],
  );

  // Determine if this cart has prescriptions with different next available dates
  const isMixedAvailabilityCart = useMemo(
    () =>
      cartItemResourceIds.some((prescriptionId) => {
        const ead = nextAvailableDatesByPrescriptionID[prescriptionId]?.earliest?.date;
        return ead && !isSameDay(parseISO(ead), parseISO(cartEarliestAvailableDate));
      }, []),
    [cartItemResourceIds, cartEarliestAvailableDate, nextAvailableDatesByPrescriptionID],
  );

  // recordCartViewedEvent to track prescription ids in the cart
  const recordCartViewedEvent = useCallback(() => {
    cartItemResourceIds.map((resourceId) => {
      trackEvent({
        event: EVENTS.CART_VIEWED,
        params: {
          origin,
          cart_prescription_ids: cartItemResourceIds,
          price_pending: isPricePending,
          ...dispensingFeeParams,
        },
        additionalFields: {
          prescriptionId: resourceId,
        },
      });
    });
  }, [trackEvent, cartItemResourceIds, origin, isPricePending, dispensingFeeParams]);

  // recordCartViewedWithNextAvailableDateEvent to track NAD info when it's valid
  const recordCartViewedWithNextAvailableDateEvent = useCallback(() => {
    cartItemResourceIds.map((resourceId) => {
      const prescription_earliest_available_date_data = nextAvailableDatesByPrescriptionID[resourceId]?.earliest;
      if (!prescription_earliest_available_date_data?.internal_reason) return;

      trackEvent({
        event: EVENTS.CART_VIEWED_WITH_NEXT_AVAILABLE_DATE,
        params: {
          cart_delayed_availability_reason: cartEarliestAvailableDateReason || null,
          cart_delayed_availability_internal_reason: cartEarliestAvailableDateInternalReason || null,
          cart_next_available_date_from_now: cartEarliestAvailableDate || null,
          origin,
          prescription_delayed_availability_internal_reason:
            prescription_earliest_available_date_data?.internal_reason || null,
          prescription_next_available_date_from_now: prescription_earliest_available_date_data?.date || null,
          bottleneck_prescriptions_ids_causing_delay_of_this_prescription_id: bottleneckPrescriptionsIds,
          cart_prescription_ids: cartItemResourceIds,
          last_request_next_available_date_at: new Date(lastRequestNextAvailableDateAttemptedAt), //lastRequestNextAvailableDateAttemptedAt is Date.now() at last NAD request
          mixed_availability_cart: isMixedAvailabilityCart,
        },
        additionalFields: {
          prescriptionId: resourceId,
        },
      });
    });
  }, [
    trackEvent,
    cartItemResourceIds,
    cartEarliestAvailableDateReason,
    cartEarliestAvailableDateInternalReason,
    cartEarliestAvailableDate,
    origin,
    bottleneckPrescriptionsIds,
    nextAvailableDatesByPrescriptionID,
    lastRequestNextAvailableDateAttemptedAt,
    isMixedAvailabilityCart,
  ]);

  // Record the cart viewed event when cart items change
  useEffect(() => {
    recordCartViewedEvent();
  }, [cartItemResourceIds.join('-')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Record the NAD info for the cart items when the cart is viewed
  useEffect(() => {
    const fetchOptions = {
      unbundledPrescriptionIDs: cartItemResourceIds,
      addressID: nextAvailableDateAddressID,
    };

    const recordNadAnalyticsWhenValid = () => {
      if (isNextAvailableDateValid) {
        recordCartViewedWithNextAvailableDateEvent();
      }
    };

    void fetchCheckoutNextAvailableDates(
      queryClient,
      dispatch,
      () => store.getState() as ReduxStateShared,
      fetchOptions,
    )
      .catch(() => Sentry.captureMessage('Failed to fetch next available date at cart', 'info'))
      .finally(recordNadAnalyticsWhenValid);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Record the NAD updates when cart items changes are made while viewing the cart
  useEffect(() => {
    if (isNextAvailableDateValid) {
      recordCartViewedWithNextAvailableDateEvent();
    }
  }, [cartItemResourceIds.join('-'), cartEarliestAvailableDate]); // eslint-disable-line react-hooks/exhaustive-deps
};
